/**
 * Tiny local query matcher for graph color groups.
 *
 * Mirrors the worker's graph-filter semantics (vaultIndex.fileMatchesQuery):
 *  - `tag:x` (repeatable): AND, case-insensitive, nested-prefix match
 *    (`tag:a` matches `a` and `a/b`).
 *  - `path:y` (repeatable): AND, case-insensitive substring of the path.
 *  - `"quoted phrase"` and remaining free text: AND, case-insensitive
 *    substring of the node title.
 *
 * Evaluated per node against title/path/tags only (no full-text) so groups
 * can be recolored instantly without round-tripping to the worker.
 */

export interface GraphGroup {
  query: string;
  color: string;
}

export interface ParsedGroupQuery {
  /** Lowercased tag filters, no '#'. */
  tags: string[];
  /** Lowercased path substrings. */
  paths: string[];
  /** Lowercased phrases + joined free text, matched against the title. */
  texts: string[];
  empty: boolean;
}

export function parseGroupQuery(q: string): ParsedGroupQuery {
  const tags: string[] = [];
  const paths: string[] = [];
  const texts: string[] = [];
  const rest = q.replace(/"([^"]*)"/g, (_all, p: string) => {
    if (p.trim()) texts.push(p.trim().toLowerCase());
    return ' ';
  });
  const words: string[] = [];
  for (const tok of rest.split(/\s+/)) {
    if (!tok) continue;
    const m = /^(tag|path):(.+)$/i.exec(tok);
    if (m) {
      if (m[1].toLowerCase() === 'tag') tags.push(m[2].replace(/^#/, '').toLowerCase());
      else paths.push(m[2].toLowerCase());
    } else {
      words.push(tok);
    }
  }
  if (words.length > 0) texts.push(words.join(' ').toLowerCase());
  return {
    tags,
    paths,
    texts,
    empty: tags.length === 0 && paths.length === 0 && texts.length === 0,
  };
}

export interface GroupTarget {
  title: string;
  path: string;
  tags: string[];
}

/** An empty query matches nothing (a freshly added group row stays neutral). */
export function matchesGroupQuery(node: GroupTarget, pq: ParsedGroupQuery): boolean {
  if (pq.empty) return false;
  for (const t of pq.tags) {
    const ok = node.tags.some((nt) => {
      const l = nt.toLowerCase();
      return l === t || l.startsWith(`${t}/`);
    });
    if (!ok) return false;
  }
  if (pq.paths.length > 0) {
    const pathL = node.path.toLowerCase();
    for (const p of pq.paths) if (!pathL.includes(p)) return false;
  }
  if (pq.texts.length > 0) {
    const titleL = node.title.toLowerCase();
    for (const txt of pq.texts) if (!titleL.includes(txt)) return false;
  }
  return true;
}

export interface ParsedGroup {
  color: string;
  pq: ParsedGroupQuery;
}

export const parseGroups = (groups: GraphGroup[]): ParsedGroup[] =>
  groups.map((g) => ({ color: g.color, pq: parseGroupQuery(g.query) }));

/** First matching group wins (Obsidian semantics). */
export function groupColorFor(node: GroupTarget, groups: ParsedGroup[]): string | undefined {
  for (const g of groups) {
    if (matchesGroupQuery(node, g.pq)) return g.color;
  }
  return undefined;
}
